import React, { useEffect, useState, useContext } from 'react';
import { Col, Row } from '@flightstats/airframe-react';
import {
  HierarchyNavigator,
  anyString,
} from '../../components/forms/HierarchyNavigator';
import { Input } from '../../components/forms/Input';
import Dropdown from '../../components/Dropdown';
import FormButtonRow from '../components/formButtonRow';
import { anyToken } from '../../constants';
import { AccountLineageGraphResponse, Attribute } from '../../types/tripGroup';
import {
  SearchTripGroupsArgs,
  OverridenValue,
} from '../../service/tripGroupService';
import { getQueryField } from '../../lib/url';
import { PageContext } from '../components/page';
import { BoxOutlinedGroupedComponents } from '../components/styledComponent';
import { MessageConfiguration } from '../../types/messageConfiguration';
import MessageConfigTripGroupDropdown from '../components/messageConfigTripGroupDropdown';
import { FormLabeledInputRowWrapper } from '../../components/forms/Input';
import { StyledCheckBox } from '../../components/forms/CheckBox';

export const isOverriddenDisplayName = 'Custom Alert Settings';

const normalizeOverriddenVal = (
  overridenVal: boolean | string,
): OverridenValue =>
  ['True', 'False', anyString].includes(String(overridenVal.toString()))
    ? (overridenVal as OverridenValue)
    : anyString;

const getSearchParamsFromQuery = (
  query: Record<string, string>,
  attributes: Attribute[],
): SearchTripGroupsArgs => {
  const attributeEntries = getQueryField(query, 'attributes')
    .split(',')
    .map(attribute => {
      return [
        attribute,
        getQueryField(query, attribute).replace(
          new RegExp(anyString, 'i'),
          anyToken,
        ),
      ];
    })
    .filter(attributeEntry => !!attributeEntry[0]);

  // fill empty attrib values w/ any token
  attributes.forEach(attr => {
    const attribute = attr.name;
    if (
      attribute !== 'ACCOUNT_ID' &&
      !attributeEntries.some(attributeEntry => attributeEntry[0] === attribute)
    ) {
      attributeEntries.push([attribute, anyToken]);
    }
  });

  const searchParams: SearchTripGroupsArgs = {
    name: getQueryField(query, 'name'),
    includeHidden:
      getQueryField(query, 'includeHidden')?.toLowerCase() === 'true',
    isOverridden: normalizeOverriddenVal(getQueryField(query, 'overridden')),
    messageConfigName: getQueryField(query, 'messageConfigName'),
    attributes: Object.fromEntries(attributeEntries),
    sort: getQueryField(query, 'sort'),
  };

  return searchParams;
};

type FlatStringObject = Record<string, string>;

interface ManageTripGroupFiltersProps {
  accountAttributeHierarchy: AccountLineageGraphResponse;
  onSubmit: (params: SearchTripGroupsArgs, isSubmitting?: boolean) => void;
  isSubmitting: boolean;
  searchParams: SearchTripGroupsArgs;
  setSearchParams: (
    params:
      | SearchTripGroupsArgs
      | ((params: SearchTripGroupsArgs) => SearchTripGroupsArgs),
  ) => void;
  messageConfigs: MessageConfiguration[];
}

export const ManageTripGroupFilters: React.FC<ManageTripGroupFiltersProps> = ({
  accountAttributeHierarchy,
  onSubmit,
  isSubmitting,
  searchParams,
  setSearchParams,
  messageConfigs,
}) => {
  const { query } = useContext(PageContext);
  const nameFilterId = 'nameFilter';
  const isOverriddenFilterId = 'isOverriddenFilter';
  const includeHiddenFilterId = 'includeHiddenFilter';
  const isOverriddenDefault = anyString;
  const isOverriddenFilterValues = [
    { text: anyString, value: anyString },
    { text: 'True', value: 'True' },
    { text: 'False', value: 'False' },
  ];

  const [doReset, setDoReset] = useState(false);

  const inputOnChange = (field: string, value: string | boolean): void => {
    setSearchParams(
      (oldSearchParams: SearchTripGroupsArgs): SearchTripGroupsArgs => {
        return {
          ...oldSearchParams,
          [field]: value,
        };
      },
    );
  };

  const onOverriddenFilterChange = (isOverridden: string) => {
    setSearchParams(
      (oldSearchParams: SearchTripGroupsArgs): SearchTripGroupsArgs => {
        return {
          ...oldSearchParams,
          isOverridden: (isOverridden || isOverriddenDefault) as OverridenValue,
        };
      },
    );
  };

  const onMessageConfigSelect = (configId: string) => {
    const value = messageConfigs.find(config => config.id === configId);
    if (!value) return;
    setSearchParams(
      (oldSearchParams: SearchTripGroupsArgs): SearchTripGroupsArgs => {
        return {
          ...oldSearchParams,
          messageConfigName: value.name,
        };
      },
    );
  };

  const onClear = () => {
    setSearchParams(
      getSearchParamsFromQuery({}, accountAttributeHierarchy.accountAttributes),
    );
    setDoReset(true);
  };

  const setAttributeFilterValues = (attributes: FlatStringObject) => {
    setSearchParams(
      (oldSearchParams: SearchTripGroupsArgs): SearchTripGroupsArgs => {
        return {
          ...oldSearchParams,
          attributes,
        };
      },
    );
  };

  useEffect(() => {
    if (!isSubmitting && Object.keys(query).length) {
      const currentSearchParams = getSearchParamsFromQuery(
        query,
        accountAttributeHierarchy.accountAttributes,
      );
      setSearchParams(currentSearchParams);
      onSubmit(currentSearchParams, false);
    }
  }, [query]);

  useEffect(() => {
    setSearchParams(
      getSearchParamsFromQuery(
        query,
        accountAttributeHierarchy.accountAttributes,
      ),
    );
  }, []);

  return (
    <>
      <Row>
        <Col style={{ flex: 'unset', marginBottom: '5px' }}>
          <FormLabeledInputRowWrapper elementId={nameFilterId} label="Name:">
            <Input
              name={nameFilterId}
              id={nameFilterId}
              value={searchParams.name}
              onChange={evt => inputOnChange('name', evt.currentTarget.value)}
              maxLength={256}
            />
          </FormLabeledInputRowWrapper>
          <FormLabeledInputRowWrapper
            elementId={isOverriddenFilterId}
            label={isOverriddenDisplayName}
          >
            <Dropdown
              id={isOverriddenFilterId}
              options={isOverriddenFilterValues}
              value={String(searchParams.isOverridden)}
              onChange={e => onOverriddenFilterChange(String(e))}
            />
          </FormLabeledInputRowWrapper>
          {messageConfigs.length > 1 && (
            <FormLabeledInputRowWrapper
              elementId="emailConfig"
              label="Email Configuration:"
            >
              <MessageConfigTripGroupDropdown
                id="emailConfig"
                onSelect={onMessageConfigSelect}
                value={
                  messageConfigs.find(
                    config =>
                      config.name ===
                      (searchParams.messageConfigName || anyString),
                  )?.id
                }
                messageConfigs={messageConfigs}
              />
            </FormLabeledInputRowWrapper>
          )}
        </Col>
        <Col style={{ flex: 'unset' }}>
          <div className="fs-exclude" data-fs-exclude>
          <BoxOutlinedGroupedComponents header="Lineage Attributes">
            <HierarchyNavigator
              hierarchy={accountAttributeHierarchy}
              filterValues={searchParams.attributes || {}}
              setFilterValues={setAttributeFilterValues}
              doReset={doReset}
              setDoReset={setDoReset}
            />
          </BoxOutlinedGroupedComponents>
          </div>
        </Col>
        <Col style={{ flex: 'unset' }}>
          <StyledCheckBox
            id={includeHiddenFilterId}
            checked={!!searchParams.includeHidden}
            onChange={() =>
              inputOnChange('includeHidden', !searchParams.includeHidden)
            }
            label="Include Hidden Groups"
          ></StyledCheckBox>
        </Col>
      </Row>
      <FormButtonRow
        onClear={onClear}
        onSubmit={() => onSubmit(searchParams, true)}
      />
    </>
  );
};
